/**
 * CTABanner Component - Brutalist Streetwear Edition
 *
 * Full-width call-to-action banner with massive typography and geometric buttons.
 * Industrial color blocks with strong visual impact.
 */

import React from 'react';
import { Button } from '../Button';

interface CTABannerProps {
  title: string;
  subtitle: string;
  buttonText: string;
  buttonLink: string;
  backgroundColor?: string;
}

export const CTABanner: React.FC<CTABannerProps> = ({
  title,
  subtitle,
  buttonText,
  buttonLink,
  backgroundColor = 'bg-amaranth_purple-500',
}) => {
  const handleButtonClick = () => {
    window.location.href = buttonLink;
  };

  return (
    <section className={`w-full ${backgroundColor} py-16 md:py-24 px-4 relative overflow-hidden`}>
      {/* Background Pattern: Geometric industrial stripes */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,0.5) 35px, rgba(255,255,255,0.5) 70px)',
          }}
        />
      </div>

      <div className="max-w-4xl mx-auto text-center relative z-10">
        {/* MASSIVE HEADING: Brutalist display font */}
        <h2
          data-aos="fade-up"
          className="font-display text-5xl md:text-6xl lg:text-7xl font-bold uppercase tracking-wider text-white mb-6"
        >
          {title}
        </h2>

        {/* SUBTITLE: Bold sans-serif */}
        <p
          data-aos="fade-up"
          data-aos-delay="100"
          className="font-body text-xl md:text-2xl text-white/90 mb-10 font-medium leading-relaxed"
        >
          {subtitle}
        </p>

        {/* GEOMETRIC BUTTON LAYOUT */}
        <div data-aos="fade-up" data-aos-delay="200" className="flex flex-col sm:flex-row gap-6 justify-center">
          <Button
            variant="outline"
            size="xl"
            onClick={handleButtonClick}
            className="bg-white border-rich_black-500 text-rich_black-500 hover:bg-rich_black-500 hover:text-white hover:border-rich_black-500"
          >
            {buttonText}
          </Button>
        </div>
      </div>
    </section>
  );
};
